import React from 'react';
import { Autocomplete, AutocompleteProps, TextField, TextFieldProps } from '@mui/material';

import { useMajorsQuery } from '../hooks/useMajorsQuery';


export type MajorAutocompleteProps = Omit<AutocompleteProps<Majors.Major, boolean, boolean, false>, 'value' | 'isOptionEqualToValue' | 'getOptionLabel' | 'renderInput' | 'options' | 'onInputChange' | 'onChange' | 'loading'> & {
  value?: CVT.MaybeNull<Majors.Major | Majors.Major[] | string | number>;
  label?: TextFieldProps['label'];
  TextFieldProps?: TextFieldProps;
  onChange: (option: CVT.MaybeNull<any>) => void;
  hideAdornment?: boolean;
  params?: Majors.GetListParams;
};

export const MajorAutocomplete: React.FC<MajorAutocompleteProps> = ({
  value,
  defaultValue,
  label,
  onChange,
  hideAdornment,
  TextFieldProps,
  params,
  ...props
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const [search, setSearch] = React.useState(params?.search || '');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { isLoading, majors } = useMajorsQuery({
    ...params,
    ...!!search && { defaultClinic: undefined },
    search,
  });

  const selectedValue = React.useMemo(() => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    return majors.find(major => major.id.toString() === value.toString()) || null;
  }, [value, majors]);

  return (
    <Autocomplete
      {...props}
      value={selectedValue}
      defaultValue={defaultValue}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return option.id.toString() === (value?.id || value).toString();
      }}
      getOptionLabel={option => {
        if (!option) return '';
        return typeof option === 'string' ? option : option.title;
      }}
      renderInput={(props) =>
        <TextField
          {...props}
          {...TextFieldProps}
          InputProps={{
            inputRef,
            ...props.InputProps,
            ...TextFieldProps?.InputProps,
            onKeyUp: (e) => {
              setTimeout(() => setSearch(inputRef.current?.value || ''), 500);
            },
          }}
          InputLabelProps={{
            ...props.InputLabelProps,
            ...TextFieldProps?.InputLabelProps,
          }}
          label={label}
        />
      }
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      options={majors}
      onChange={(e, value) => {
        if (typeof value !== 'string') {
          onChange(value);
        }
      }}
      filterOptions={(option) => option}
      loading={isLoading}
      noOptionsText="No majors found"
      slotProps={{
        paper: {
          elevation: 2,
        },
        chip: {
          variant: 'filled',
        },
      }}
    />
  );
};
